import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { addToUser } from "../../redux/actions/userAction";
import { AppInstance } from "../../api/instance";
import {
  FALLBACK_COUNTRY_CODES,
  fetchCountryDialData,
  pickDefaultDialCode,
} from "../../helpers/countryDialCodes";
import "./AuthModal.scss";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isOtpAlreadySentMessage = (message) =>
  String(message || "")
    .toLowerCase()
    .includes("otp already sent");

const normalizeVerifyPayload = (data) => {
  const token = data?.data?.token ?? data?.token;
  const user = data?.data?.user ?? data?.user;
  return {
    success: data?.success !== false,
    token,
    user,
  };
};

const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentScreen, setCurrentScreen] = useState("identifier");
  const [formData, setFormData] = useState({
    identifier: "",
    otp: "",
    country_code: "+971",
  });
  const [countryCodeOptions, setCountryCodeOptions] = useState(
    FALLBACK_COUNTRY_CODES
  );
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [otpInputs, setOtpInputs] = useState(["", "", "", "", "", ""]);
  const [onSave, setOnSave] = useState(false);
  const [formError, setFormError] = useState({});
  const [otpTimer, setOtpTimer] = useState(0);
  /** Username sent to /auth/otp (email string or local phone digits). */
  const [usernameForFlow, setUsernameForFlow] = useState("");
  const [inputType, setInputType] = useState("");
  const [showCountrySelect, setShowCountrySelect] = useState(false);
  const [signupId, setSignupId] = useState("");
  const addToast = () => {};
  const dispatch = useDispatch();

  const loadCountries = useCallback(async () => {
    setCountriesLoading(true);
    try {
      const { dialOptions } = await fetchCountryDialData(AppInstance);
      if (dialOptions.length > 0) {
        setCountryCodeOptions(dialOptions);
        setFormData((prev) => ({
          ...prev,
          country_code:
            prev.country_code && dialOptions.some((o) => o.code === prev.country_code)
              ? prev.country_code
              : pickDefaultDialCode(dialOptions),
        }));
      }
    } catch {
      /* keep fallback */
    } finally {
      setCountriesLoading(false);
    }
  }, []);

  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  useEffect(() => {
    if (isOpen) {
      const dropdowns = document.querySelectorAll(
        ".account-dropdown, .search-content, .shopping-cart-content"
      );
      dropdowns.forEach((dropdown) => {
        dropdown.classList.remove("active");
      });
      loadCountries();
    }
  }, [isOpen, loadCountries]);

  const resetFlowState = useCallback(() => {
    setFormData((prev) => ({
      identifier: "",
      otp: "",
      country_code: pickDefaultDialCode(countryCodeOptions),
    }));
    setFormError({});
    setOtpTimer(0);
    setInputType("");
    setShowCountrySelect(false);
    setOtpInputs(["", "", "", "", "", ""]);
    setSignupId("");
    setUsernameForFlow("");
    setCurrentScreen("identifier");
  }, [countryCodeOptions]);

  useEffect(() => {
    if (!isOpen) {
      resetFlowState();
    }
  }, [isOpen, resetFlowState]);

  const startOtpTimer = () => setOtpTimer(60);

  const handleIdentifierChange = (e) => {
    const raw = e.target.value;
    const name = e.target.name;
    setFormData((prev) => ({ ...prev, [name]: raw }));

    if (formError.identifier || formError.phone) {
      setFormError((err) => ({ ...err, identifier: "", phone: "" }));
    }

    const trimmed = raw.trim();
    if (!trimmed) {
      setInputType("");
      setShowCountrySelect(false);
      return;
    }
    if (/^\d/.test(trimmed)) {
      setInputType("phone");
      setShowCountrySelect(true);
    } else if (/^[a-zA-Z]/.test(trimmed)) {
      setInputType("email");
      setShowCountrySelect(false);
    }
  };

  const handleCountryCodeChange = (e) => {
    setFormData((prev) => ({ ...prev, country_code: e.target.value }));
    if (formError.phone) setFormError((err) => ({ ...err, phone: "" }));
  };

  const handleOtpInputChange = (index, value) => {
    if (value.length > 1) return;
    const newOtpInputs = [...otpInputs];
    newOtpInputs[index] = value;
    setOtpInputs(newOtpInputs);
    const otpValue = newOtpInputs.join("");
    setFormData((prev) => ({ ...prev, otp: otpValue }));
    if (formError.otp) setFormError((err) => ({ ...err, otp: "" }));
    if (value && index < 5) {
      const nextInput = document.getElementById(`auth-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpInputs[index] && index > 0) {
      const prevInput = document.getElementById(`auth-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const buildRequestOtpBody = useCallback(() => {
    const raw = formData.identifier.trim();
    const isEmail = EMAIL_RE.test(raw);
    if (isEmail) {
      return { username: raw, platform: "web" };
    }
    const digits = raw.replace(/\D/g, "");
    if (!digits) return null;
    return {
      username: digits,
      country_code: formData.country_code,
      platform: "web",
    };
  }, [formData.identifier, formData.country_code]);

  const handleSendOtp = async () => {
    const raw = formData.identifier.trim();
    if (!raw) {
      setFormError({ identifier: "Email or mobile number is required" });
      return;
    }

    const isEmail = EMAIL_RE.test(raw);
    if (!isEmail) {
      const digits = raw.replace(/\D/g, "");
      if (!digits) {
        setFormError({ identifier: "Enter a valid mobile number" });
        return;
      }
      if (!formData.country_code) {
        setFormError({ phone: "Country code is required for mobile" });
        return;
      }
    }

    const body = buildRequestOtpBody();
    if (!body) {
      setFormError({ identifier: "Enter a valid email or mobile number" });
      return;
    }

    setOnSave(true);
    try {
      const { data } = await AppInstance.post("/auth/otp", body);
      if (data?.success) {
        setUsernameForFlow(body.username);
        setSignupId(data.signup_id || "");
        setCurrentScreen("otp");
        startOtpTimer();
        setFormError({});
        return;
      }
      const apiMessage = data?.message || "";
      if (isOtpAlreadySentMessage(apiMessage)) {
        setUsernameForFlow(body.username);
        setSignupId(data?.signup_id || "");
        setCurrentScreen("otp");
        startOtpTimer();
        setFormError({});
        return;
      }
      setFormError({
        identifier: apiMessage || "Failed to send OTP. Please try again.",
      });
    } catch (error) {
      let errorMessage =
        error.response?.data?.message || "Something went wrong. Please try again.";
      if (isOtpAlreadySentMessage(errorMessage)) {
        const retryBody = buildRequestOtpBody();
        if (retryBody) {
          setUsernameForFlow(retryBody.username);
          setSignupId(error.response?.data?.signup_id || "");
          setCurrentScreen("otp");
          startOtpTimer();
          setFormError({});
          return;
        }
      }
      if (error.response?.data?.errors?.username) {
        errorMessage = Array.isArray(error.response.data.errors.username)
          ? error.response.data.errors.username[0]
          : error.response.data.errors.username;
      }
      setFormError({ identifier: errorMessage });
    } finally {
      setOnSave(false);
    }
  };

  const handleIdentifierSubmit = (e) => {
    e.preventDefault();
    setFormError({});
    handleSendOtp();
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!formData.otp || formData.otp.length < 6) {
      setFormError({ otp: "Enter the 6-digit code" });
      return;
    }

    setOnSave(true);
    setFormError({});

    const base = { otp_code: formData.otp, platform: "web" };
    const idTrim = formData.identifier.trim();
    let verifyBody;
    if (signupId) {
      verifyBody = { ...base, signup_id: signupId };
      if (idTrim) verifyBody.username = idTrim;
    } else {
      verifyBody = { ...base, username: usernameForFlow };
    }

    try {
      const { data } = await AppInstance.post("/auth/otp/verify", verifyBody);
      if (data?.success) {
        const n = normalizeVerifyPayload(data);
        if (n.token) {
          localStorage.setItem("token", n.token);
          dispatch(
            addToUser({ success: true, token: n.token, user: n.user }, addToast)
          );
          onSuccess && onSuccess();
          onClose();
        } else {
          setFormError({ otp: data?.message || "Could not complete sign-in." });
        }
      } else {
        setFormError({ otp: data?.message || "Invalid OTP. Please try again." });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Something went wrong. Please try again.";
      setFormError({ otp: errorMessage });
    } finally {
      setOnSave(false);
    }
  };

  const handleResendOtp = () => {
    if (otpTimer > 0) return;
    const body = buildRequestOtpBody();
    if (!body) return;
    handleSendOtp();
  };

  const handleBackToIdentifier = () => {
    setCurrentScreen("identifier");
    setFormData((prev) => ({ ...prev, otp: "" }));
    setFormError({});
    setOtpTimer(0);
    setOtpInputs(["", "", "", "", "", ""]);
    setSignupId("");
    setUsernameForFlow("");
  };

  if (!isOpen) return null;

  const headerTitle =
    currentScreen === "identifier" ? "Log in" : "Enter verification code";

  return (
    <div className="auth-modal-overlay" role="presentation">
      <button
        type="button"
        className="auth-modal-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        {currentScreen === "identifier" ? (
          <div className="auth-modal-header auth-modal-header--cartlike">
            <h2 id="auth-modal-title">{headerTitle}</h2>
            <button
              type="button"
              className="cart-drawer-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="pe-7s-close" />
            </button>
          </div>
        ) : (
          <div className="auth-modal-header">
            <div className="auth-modal-header__lead">
              <button
                type="button"
                className="auth-modal-back"
                onClick={handleBackToIdentifier}
                aria-label="Back"
              >
                ← Back
              </button>
            </div>
            <h2 id="auth-modal-title">{headerTitle}</h2>
            <button
              type="button"
              className="cart-drawer-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="pe-7s-close" />
            </button>
          </div>
        )}

          {currentScreen === "identifier" && (
            <form onSubmit={handleIdentifierSubmit} className="auth-modal-form">
              <div className="auth-modal-body cart-drawer-body">
                <div className="auth-screen">
                  {formError.identifier && (
                    <div className="error-message-container">
                      <span className="error-message">{formError.identifier}</span>
                    </div>
                  )}
                  {formError.phone && (
                    <div className="error-message-container">
                      <span className="error-message">{formError.phone}</span>
                    </div>
                  )}

                  <div className="form-group auth-identifier-row">
                    {showCountrySelect && (
                      <select
                        className="auth-country-select"
                        name="country_code"
                        value={formData.country_code}
                        onChange={handleCountryCodeChange}
                        disabled={countriesLoading}
                        aria-label="Country code"
                      >
                        {countryCodeOptions.map((opt) => (
                          <option key={opt.code} value={opt.code}>
                            {opt.code}
                          </option>
                        ))}
                      </select>
                    )}
                    <input
                      type="text"
                      name="identifier"
                      autoComplete="username"
                      placeholder="Email or mobile number"
                      value={formData.identifier}
                      onChange={handleIdentifierChange}
                      className={`auth-identifier-input ${
                        formError.identifier || formError.phone ? "error" : ""
                      } ${showCountrySelect ? "auth-identifier-input--with-code" : ""}`}
                    />
                  </div>
                </div>
              </div>
              <div className="cart-drawer-footer auth-modal-footer">
                <div className="cart-drawer-actions btn-hover">
                  <button
                    type="submit"
                    className="default-btn auth-modal-primary-btn w-100 text-center d-block"
                    disabled={onSave || countriesLoading}
                  >
                    {onSave ? "Sending…" : "Continue"}
                  </button>
                </div>
              </div>
              <p className="auth-modal-privacy-bar">
                By continuing, you agree to our{" "}
                <a
                  href={`${process.env.PUBLIC_URL || ""}/privacy-policy`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          )}

          {currentScreen === "otp" && (
            <form onSubmit={handleOtpSubmit} className="auth-modal-form">
              <div className="auth-modal-body cart-drawer-body">
                <div className="auth-screen auth-screen--otp">
                  <p className="auth-otp-subtitle">
                    Enter the 6-digit code sent to{" "}
                    <strong>
                      {inputType === "email"
                        ? usernameForFlow
                        : `${formData.country_code} ${usernameForFlow}`}
                    </strong>
                  </p>

                  {formError.otp && (
                    <div className="error-message-container">
                      <span className="error-message">{formError.otp}</span>
                    </div>
                  )}

                  <div className="otp-inputs-container">
                    {otpInputs.map((value, index) => (
                      <input
                        key={index}
                        id={`auth-otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={value}
                        onChange={(e) =>
                          handleOtpInputChange(index, e.target.value.replace(/\D/g, ""))
                        }
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className={`otp-input ${formError.otp ? "error" : ""}`}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="cart-drawer-footer auth-modal-footer auth-modal-footer--otp">
                <div className="auth-modal-otp-footer-stack">
                  <div className="auth-modal-otp-verify-section">
                    <div className="cart-drawer-actions btn-hover">
                      <button
                        type="submit"
                        className="default-btn auth-modal-primary-btn w-100 text-center d-block"
                        disabled={onSave}
                      >
                        {onSave ? "Verifying…" : "Verify"}
                      </button>
                    </div>
                  </div>
                  <hr className="auth-modal-otp-divider" aria-hidden="true" />
                  <div className="auth-modal-otp-resend-section">
                    <p className="auth-otp-helper">
                      {otpTimer > 0
                        ? `Resend code in ${otpTimer}s`
                        : "Didn’t receive a code?"}
                    </p>
                    <div className="cart-drawer-actions btn-hover">
                      <button
                        type="button"
                        className="default-btn auth-modal-otp-resend-btn w-100 text-center d-block"
                        onClick={handleResendOtp}
                        disabled={otpTimer > 0 || onSave}
                      >
                        Resend code
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
      </div>
    </div>
  );
};

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default AuthModal;
